import { format } from 'date-fns'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Accordion } from '../../components/accordion'
import { BackButton } from '../../components/button/backButton'
import { CategoryCard } from '../../components/cards/categoryCard'
import { Status } from '../../components/cards/statusTag'
import { AvailableHoursView } from '../../components/common/availableHoursView'
import { CreatorSectionSummary } from '../../components/common/creator-section-summary'
import { SettlementAccountDetails } from '../../components/common/settlementAccountDetails'
import { VendorOffsiteCreator } from '../../components/common/vendor-offsite-creator'
import { VendorAddress } from '../../components/common/vendorAddress'
import { VendorDeliveryAreaRestriction } from '../../components/common/vendorDeliveryAreaRestriction'
import { VendorDeliveryRegions } from '../../components/common/vendorDeliveryRegions'
import { VendorServiceChargeManagement } from '../../components/common/vendorFeeManagement'
import { VendorFulfilmentCenter } from '../../components/common/vendorFulfilmentCenter'
import { VendorInventoryMode } from '../../components/common/vendorInventoryMode'
import { VendorSettlementConfiguration } from '../../components/common/vendorSettlementConfiguration'
import { VendorSplitConfigiration } from '../../components/common/vendorSplitConfigiration'
import { VendorStakeholderPercentage } from '../../components/common/vendorStakeholderPercentage'
import Icon from '../../components/icon'
import { ImageBanner } from '../../components/images/imageBanner'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import { EditInput } from '../../components/inputs/editInput'
import { ImageUploadPreviewModal } from '../../components/modal/app_modals/ImagePreviewModal'
import { SelectCategoriesModal } from '../../components/modal/app_modals/selectCategoriesModal'
import Spinner from '../../components/spinner/Spinner'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import { DateFilterQueryProps } from '../../redux-toolkits/shared/shared.type'
import {
  useGetSingleVendorQuery,
  useSetVendorDeliveryRegionsMutation,
  useUpdateVendorDetailsMutation,
  useUpdateVendorSettlementAccountMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import { CreatorVideos } from './creator-videos'
import { SingleVendorChannelBreakdown } from './singleVendorChannelBreakdown'
import { SingleVendorCuratedProducts } from './singleVendorCuratedProducts'
import { SingleVendorMetrics } from './singleVendorMetrics'
import { SingleVendorProducts } from './singleVendorProducts'
import { SingleVendorSettlements } from './singleVendorSettlements'

export const SingleVendorPage: React.FC = () => {
  const [editIndex, setEditIndex] = useState<number>()
  const [uploadedBanner, setUploadedBanner] = useState<File>()
  const [uploadedLogo, setUploadedLogo] = useState<File>()
  const [uploadMode, setUploadMode] = useState('')
  const [businessInformation, setBusinessInformation] = useState<
    {
      key: string
      title: string
      value: string
      type: 'text' | 'textarea'
      placeholder: string
      maxLength?: number
    }[]
  >([])
  const [dateFilterProps, setDateFilterProps] = useState<DateFilterQueryProps>({
    filter: 'today',
    isCustomDateRange: false,
    startDate: undefined,
    endDate: undefined,
  })

  const { id } = useParams()

  const [searchParams, setSearchParams] = useSearchParams()

  const [updateVendor, { isLoading: updatingVendor }] =
    useUpdateVendorDetailsMutation()

  const [updateSettlementAccountt, { isLoading: updatingSettlementAccount }] =
    useUpdateVendorSettlementAccountMutation()

  const [setVendorDeliveryRegions, { isLoading: updatingDeliveryRegions }] =
    useSetVendorDeliveryRegionsMutation()

  const {
    data: vendor,
    isLoading,
    refetch,
  } = useGetSingleVendorQuery({
    id: id ?? '',
  })

  const setBusinessInformationData = () => {
    setBusinessInformation([
      {
        key: 'businessName',
        title: 'Business name',
        value: vendor?.data?.profile?.businessInformation?.businessName ?? '',
        placeholder: 'Enter business name',
        type: 'text',
      },
      {
        key: 'businessEmail',
        title: 'Business email address',
        value: vendor?.data?.profile?.businessInformation?.businessEmail ?? '',
        placeholder: 'Enter business email address',
        type: 'text',
      },
      {
        key: 'businessPhonenumber',
        title: 'Business phone number',
        value:
          vendor?.data?.profile?.businessInformation?.businessPhonenumber ?? '',
        placeholder: 'Enter business phone number',
        type: 'text',
      },
      {
        key: 'businessDescription',
        title: 'Business bio',
        value:
          vendor?.data?.profile?.businessInformation?.businessDescription ?? '',
        placeholder: 'Enter business bio',
        type: 'textarea',
      },
      {
        key: 'extendedBio',
        title: 'Business short bio',
        value: vendor?.data?.profile?.businessInformation?.extendedBio ?? '',
        placeholder: 'Enter business bio',
        type: 'textarea',
        maxLength: 140,
      },
      {
        key: 'tinNumber',
        title: 'Tax Identification Number',
        value: vendor?.data?.profile?.businessInformation?.tinNumber ?? '',
        placeholder: 'Enter business TIN',
        type: 'text',
      },
      {
        key: 'cacNumber',
        title: 'CAC Number',
        value: vendor?.data?.profile?.businessInformation?.cacNumber ?? '',
        placeholder: 'Enter business CAC number',
        type: 'text',
      },
    ])
  }

  const updateValueByKey = (key: string, newValue: string) => {
    setBusinessInformation((prevData) => {
      return prevData.map((item) =>
        item.key === key ? { ...item, value: newValue } : item,
      )
    })
  }

  const handleUpdateVendor = async (data?: object, callback = () => {}) => {
    const payload = businessInformation.reduce(
      (acc: { [key: string]: string }, { key, value }) => {
        acc[key] = value
        return acc
      },
      {},
    )

    const resp = await updateVendor({
      id: id ?? '',
      data: Object.entries({ ...payload, ...(data ?? {}) }).reduce(
        (acc: Record<string, string>, [key, value]) => {
          if (value !== '') acc[key] = value
          return acc
        },
        {} as Record<string, string>,
      ),
    })

    if (resp.data?.success) {
      callback()
      setEditIndex(undefined)
    }
  }

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    bannerOrIcon: 'logo' | 'banner' = 'banner',
  ) => {
    if (!event.target.files) {
      return
    }
    setUploadMode(bannerOrIcon)
    const selectedFile = event.target.files[0]
    bannerOrIcon === 'banner'
      ? setUploadedBanner(selectedFile)
      : setUploadedLogo(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  useEffect(() => {
    setBusinessInformationData()
  }, [vendor])

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setDateFilterProps({
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
    })
  }, [searchParams])

  return (
    <Layout>
      {isLoading ? (
        <Spinner fullScreen={false} />
      ) : (
        <div className="flex flex-col gap-10">
          <BackButton />

          <div className="flex flex-col gap-2 text-sec-black">
            {/* Banner and logo  */}
            <div>
              <ImageBanner
                imageUrl={
                  vendor?.data?.profile?.businessInformation?.banner ?? ''
                }
                loading={false}
                className="!max-h-[280px]"
                onImageEdit={(e) => {
                  handleFileChange(e, 'banner')
                }}
                editIconPos="top-right"
              />

              <ImageBanner
                imageUrl={vendor?.data?.profile?.logo ?? ''}
                loading={false}
                className="-mt-10 z-10 relative ml-4 !rounded-full !max-w-[80px] !max-h-[80px]"
                onImageEdit={(e) => {
                  handleFileChange(e, 'logo')
                }}
                editIconPos="bottom-right"
                initials={`${vendor?.data?.profile?.businessInformation?.businessName.charAt(
                  0,
                )}`}
              />
            </div>

            <p className="text-[40px] text-black font-recoleta font-semibold">
              {vendor?.data?.profile?.businessInformation?.businessName}
            </p>
            <p>
              Joined{' '}
              {vendor?.data?.profile?.businessInformation?.createdAt
                ? format(
                    vendor?.data?.profile?.businessInformation?.createdAt,
                    'MMM dd, yyyy',
                  )
                : ''}
            </p>
            <p className="capitalize">
              Owned by {vendor?.data?.profile?.firstName}{' '}
              {vendor?.data?.profile?.lastName}
            </p>
            <div className="flex items-center gap-2">
              <Icon id="envelope" width={24} height={24} />
              <p>{vendor?.data?.profile?.businessInformation?.businessEmail}</p>
            </div>
            <div className="flex items-center gap-2">
              <Icon id="phone-icon" width={24} height={24} />
              <p>
                {
                  vendor?.data?.profile?.businessInformation
                    ?.businessPhonenumber
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              <p>Creator&apos;s business status:</p>
              <Status
                text={vendor?.data?.profile?.status ?? ''}
                type={
                  vendor?.data?.profile?.status === 'active'
                    ? 'success'
                    : 'fail'
                }
              />
            </div>
          </div>

          <Accordion title="Creator Information">
            <div className="mt-6 flex flex-col gap-4">
              {businessInformation.map((info, idx) => {
                const key = `${info.key}-${idx}`

                return (
                  <EditInput
                    title={info.title}
                    value={info.value}
                    onChange={(e) => {
                      updateValueByKey(info.key, e.target.value)
                    }}
                    type={info.type}
                    placeholder={info.placeholder}
                    onEditSave={() => {
                      handleUpdateVendor()
                    }}
                    editing={editIndex === idx}
                    onEditClick={() => {
                      setEditIndex(idx)
                    }}
                    onCancel={() => {
                      setEditIndex(undefined)
                      setBusinessInformationData()
                    }}
                    loading={updatingVendor}
                    className="pb-4 border-b"
                    inputName=""
                    errors={undefined}
                    mode="edit"
                    key={key}
                    maxLength={info.maxLength}
                  />
                )
              })}

              <VendorAddress
                address={
                  vendor?.data?.profile?.businessInformation?.businessAddress
                    ?.address
                }
                loading={updatingVendor}
                onModify={async ({ data: { placeId, ...data }, close }) => {
                  await handleUpdateVendor(
                    {
                      businessAddress: data,
                    },
                    () => {
                      close()
                    },
                  )
                }}
                error={undefined}
                className="pb-4 border-b"
              />

              <EditInput
                title="Business Category"
                onEditSave={() => {}}
                editing={false}
                onEditClick={() => {
                  handleModalOpen('manageCategoriesModal')
                }}
                onCancel={() => {}}
                loading={false}
                className="pb-4 border-b"
              >
                {() => {
                  return (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-6 flex-wrap">
                        {[0, undefined].includes(
                          vendor?.data?.profile?.businessInformation?.category
                            ?.length,
                        ) ? (
                          <p className="text-sec-black">No category added</p>
                        ) : (
                          vendor?.data?.profile?.businessInformation?.category.map(
                            (cat, idx) => {
                              return (
                                <CategoryCard
                                  category={cat}
                                  key={`${cat?._id}-${idx}`}
                                />
                              )
                            },
                          )
                        )}
                      </div>
                      <CreatorSectionSummary
                        categories={
                          vendor?.data?.profile?.businessInformation?.category
                        }
                      />
                    </div>
                  )
                }}
              </EditInput>

              <VendorDeliveryRegions
                vendor={vendor?.data?.profile}
                loading={updatingDeliveryRegions}
                onSuccess={async (data, closeCallback) => {
                  const response = await setVendorDeliveryRegions({
                    id: id ?? '',
                    regions: data.regions.map((region) => ({
                      regionId: region.regionId,
                      deliveryTime: region.deliveryTime,
                    })),
                  })

                  if ('data' in response && response.data?.success) {
                    closeCallback()
                  }
                }}
                className="pb-4 border-b"
              />

              <VendorDeliveryAreaRestriction
                vendor={vendor?.data?.profile}
                loading={updatingVendor}
                onModify={async (data, handleClose) => {
                  await handleUpdateVendor(data, () => {
                    handleClose()
                  })
                }}
                className="pb-4 border-b"
              />

              <AvailableHoursView
                availableHours={
                  vendor?.data?.profile?.businessInformation?.availableHours
                }
                loading={updatingVendor}
                onModify={async (availHours) => {
                  await handleUpdateVendor({ availableHours: availHours })
                }}
                className="pb-4 border-b"
              />

              <SettlementAccountDetails
                vendor={vendor?.data.profile}
                loading={updatingSettlementAccount}
                onModify={async (settlementAccount) => {
                  await updateSettlementAccountt({
                    id: id ?? '',
                    data: {
                      settlementAccount: {
                        accountNumber: settlementAccount.accountNumber,
                        bankCode: settlementAccount.bankCode,
                      },
                    },
                  })
                }}
                className="pb-4 border-b"
              />

              <VendorFulfilmentCenter
                fulfilmentCenters={vendor?.data?.profile?.fulfilmentCentres}
                loading={updatingVendor}
                onModify={async (data, handleClose) => {
                  await handleUpdateVendor({
                    fulfilmentCentres: data.fulfilmentCenters.map((f) => f._id),
                  }).then(() => {
                    handleClose()
                  })
                }}
                className="pb-4 border-b"
              />

              <VendorInventoryMode
                vendor={vendor?.data?.profile}
                onSuccess={refetch}
                className="pb-4 border-b"
              />

              <VendorOffsiteCreator
                vendor={vendor?.data?.profile}
                loading={updatingVendor}
                onModify={async (data, handleClose) => {
                  await handleUpdateVendor(data, () => {
                    handleClose()
                  })
                }}
                className="pb-4 border-b"
              />

              <VendorServiceChargeManagement
                vendorId={vendor?.data?.profile._id}
                fee={vendor?.data?.serviceCharge}
                className="pb-4 border-b"
              />

              <VendorSettlementConfiguration
                vendor={vendor?.data?.profile}
                className="pb-4 border-b"
                refetch={() => {
                  refetch()
                }}
              />

              <VendorSplitConfigiration
                vendor={vendor?.data?.profile}
                className="pb-4 border-b"
                refetch={() => {
                  refetch()
                }}
              />

              <VendorStakeholderPercentage
                vendor={vendor?.data?.profile}
                className="pb-4 border-b"
                refetch={() => {
                  refetch()
                }}
              />
            </div>
          </Accordion>

          {id ? <SingleVendorCuratedProducts creatorId={id} /> : null}

          {id ? <SingleVendorProducts creatorId={id} /> : null}

          {id ? <CreatorVideos creatorId={id} /> : null}

          {id ? (
            <DateFilterNav
              filter={dateFilterProps?.filter ?? 'custom'}
              isCustomDateRange={dateFilterProps?.isCustomDateRange ?? false}
              startDate={dateFilterProps?.startDate}
              endDate={dateFilterProps?.endDate}
              setFilter={(filter) => {
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  filter: filter.filter ?? '',
                  isCustomDateRange: String(filter.isCustomDateRange),
                  endDate: filter.endDate ?? '',
                  startDate: filter.startDate ?? '',
                })
              }}
              className="w-fit"
            />
          ) : null}

          {id ? (
            <SingleVendorMetrics
              id={id}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              dateFilterProps={dateFilterProps}
            />
          ) : undefined}

          {id ? (
            <SingleVendorChannelBreakdown
              id={id}
              dateFilterProps={dateFilterProps}
            />
          ) : undefined}

          {id ? (
            <SingleVendorSettlements
              id={id}
              profile={vendor?.data?.profile}
              dateFilterProps={dateFilterProps}
            />
          ) : undefined}
        </div>
      )}

      {uploadedBanner || uploadedLogo ? (
        <ImageUploadPreviewModal
          successToast={false}
          image={uploadedBanner || (uploadedLogo as File)}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            uploadedBanner
              ? setUploadedBanner(undefined)
              : setUploadedLogo(undefined)
          }}
          uploadMode={uploadMode}
          onSuccessCallback={(url) => {
            if (uploadedBanner) {
              handleUpdateVendor({
                banner: url,
              })
            } else {
              handleUpdateVendor({
                logo: url,
              })
            }
          }}
        />
      ) : null}

      <SelectCategoriesModal
        open={modalOpen && modal === 'manageCategoriesModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async (categories) => {
          handleUpdateVendor(
            {
              category: categories.map((c) => c._id || undefined),
            },
            () => {
              handleModalClose()
            },
          )
        }}
        selectedCategories={
          vendor?.data?.profile?.businessInformation?.category ?? []
        }
        loading={updatingVendor}
        buttonText="Save"
      />
    </Layout>
  )
}
